import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { Loader2, MapPin } from "lucide-react";

/**
 * Autouzupełnianie z bazy TERYT.
 * mode="simc" - wyszukuje miejscowosc, onSelect zwraca {sym, city, woj_name}
 * mode="ulic" - wyszukuje ulice w wybranej miejscowosci (wymaga simcSym)
 */
export default function TerytAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  testId,
  error,
  mode = "simc",
  simcSym = null,
  disabled = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const query = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if (mode === "ulic" && !simcSym) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const url = mode === "simc" ? "/address/simc" : "/address/ulic";
        const params = mode === "simc" ? { q } : { sym: simcSym, q };
        const res = await api.get(url, { params });
        setSuggestions(res.data || []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    query(v);
  };

  const pick = (s) => {
    onSelect(s);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        data-testid={testId}
        value={value}
        onChange={handleChange}
        onFocus={() => value?.length >= 2 && suggestions.length > 0 && setOpen(true)}
        disabled={disabled}
        className={`rm-input mt-1 pr-10 ${error ? "rm-input-error" : ""} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        placeholder={placeholder}
        autoComplete="off"
      />
      {loading && (
        <Loader2 size={16} className="animate-spin absolute right-3 top-1/2 mt-0.5 -translate-y-1/2 text-slate-400" />
      )}
      {open && suggestions.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl max-h-72 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => pick(s)}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-start gap-2"
            >
              <MapPin size={14} className="text-[#1f3a8a] mt-1 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">
                  {mode === "simc" ? s.city : s.street}
                </div>
                {mode === "simc" && (
                  <div className="text-xs text-slate-500 truncate">
                    {s.is_part && <span className="text-[10px] uppercase font-bold text-slate-400 mr-1">część</span>}
                    {s.gmi_name && `gm. ${s.gmi_name}`}
                    {s.gmi_name && s.woj_name && ", "}
                    {s.woj_name && `woj. ${s.woj_name.toLowerCase()}`}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
